import { ColumnTable } from 'arquero'
import { PinnedMorphotype } from 'redux/slices'
import { getColumn } from '../arrow/arqueroHelpers'

interface CreateMorphotypeFromClusterFromDataFrameParams {
  clusterValue: string
  dataFrame: ColumnTable
  existingMorphotypes: PinnedMorphotype[]
  prefilteredCellIdSet: Set<string>
}

/**
 * Creates a new cluster-based morphotype from a Leiden cluster using DataFrames (memory-efficient)
 * Works directly with columnar data without building cellInfo array
 * Only includes cells that are visible (not hidden by prefiltering)
 * @returns The new morphotype, or undefined if creation failed
 */
const createMorphotypeFromClusterFromDataFrame = ({
  clusterValue,
  dataFrame,
  existingMorphotypes,
  prefilteredCellIdSet,
}: CreateMorphotypeFromClusterFromDataFrameParams): PinnedMorphotype | undefined => {
  // Extract columns from DataFrame using cached helper
  const cellIds = getColumn<string>(dataFrame, 'CELL_ID')
  const leidenValues = getColumn<string | number>(dataFrame, 'leiden')
  const umapXValues = getColumn<number>(dataFrame, 'UMAP_0')
  const umapYValues = getColumn<number>(dataFrame, 'UMAP_1')

  if (cellIds.length === 0 || leidenValues.length === 0 ||
      umapXValues.length === 0 || umapYValues.length === 0) {
    return undefined
  }

  // Filter cells by cluster value and build points array
  const points = []
  for (let i = 0; i < cellIds.length; i += 1) {
    const cellId = cellIds[i]
    const leidenValue = leidenValues[i]

    // Only process non-prefiltered cells
    if (!prefilteredCellIdSet.has(cellId)) {
      // Convert leiden to string for comparison
      const cellLeidenStr = leidenValue !== undefined && leidenValue !== null
        ? String(leidenValue).trim()
        : ''

      // Only include cells in the selected cluster
      if (cellLeidenStr === clusterValue) {
        const x = Number(umapXValues[i])
        const y = Number(umapYValues[i])

        // Add cells with valid coordinates
        if (!Number.isNaN(x) && !Number.isNaN(y)) {
          points.push({
            x,
            y,
            cellId,
          })
        }
      }
    }
  }

  // Validate we have valid points
  if (points.length === 0) {
    return undefined
  }

  // Generate a unique ID that avoids collisions
  const existingIds = existingMorphotypes.map((p) => p.id)
  const newId = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1

  // Create the new pinned morphotype
  const newMorphotype: PinnedMorphotype = {
    id: newId,
    name: `Leiden Cluster ${clusterValue}`,
    cells: {
      points,
    },
    active: true,
    isClusterBased: true,
    leidenCluster: clusterValue,
  }

  return newMorphotype
}

export default createMorphotypeFromClusterFromDataFrame
